import { flash, FormGroup, Input, Label, SubmitButton } from "@xola/ui-kit";
import axios, { AxiosRequestConfig } from "axios";
import { FormField, InputField, requiredString } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Factor, Permission, Seller, StripeBankAccount, UserType } from "types";
import { object, string } from "yup";
import { api } from "../../../api/api";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useModal, useVisibility } from "../../../hooks/useVisibility";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { MultiFactorAuthenticationInfoModal } from "../MyProfile/MultiFactorAuthenticationCheck";
import { MultiFactorAuthenticationForBankInfoModal } from "../MyProfile/MultiFactorAuthenticationForBankInfoModal";
import { AuthenticationFor, MultiFactorAuthenticationModal } from "../MyProfile/MultiFactorAuthenticationModal";

const validationSchema = object().shape({
    routing_number: string()
        .required()
        .matches(/^\d+$/, "must be only digits")
        .when("country", {
            is: "US",
            then: string().min(9, "must be exactly 9 digits").max(9, "must be exactly 9 digits"),
        }),
    account_number: requiredString(),
});

const getStripeBankAccount = (seller: Seller) => {
    return seller.stripeAccount?.data?.external_accounts?.data?.[0];
};

const getStripeAccount = (seller: Seller) => {
    return seller.stripeAccount;
};

const getInitialValues = (seller: Seller): StripeBankAccount => {
    const stripeBankAccount = getStripeBankAccount(seller);

    return {
        id: stripeBankAccount?.id,
        currency: seller.currency.toLowerCase(),
        country: seller.countryCode,
        account_holder_name: stripeBankAccount?.account_holder_name ?? "",
        routing_number: stripeBankAccount?.routing_number ?? "",
        account_holder_type: seller.stripeAccount?.business_type ?? "company",
        account_number: "", // Always to be empty when form is editable
    };
};

export const BankAccount = () => {
    const seller = useSeller();
    const config: AxiosRequestConfig = { ignoreUnauthorizedErrorInterceptor: true };
    const stripeAccount = getStripeAccount(seller);
    const stripeBankAccount = getStripeBankAccount(seller);
    const [isEditable, setIsEditable] = useState(!(stripeBankAccount?.routing_number ?? stripeBankAccount?.last4));
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const hasMFAAccess = isGranted(Permission.ADD_MULTI_FACTOR_AUTHENTICATION_VIEW);
    const [bankAccountPayload, setBankAccountPayload] = useState<StripeBankAccount>();
    const [showMFAInfo, { toggle: toggleshowMFAInfo }] = useVisibility(false);
    const showMFAModal = useModal(false);

    const user = useUser();
    const isUserSeller = user.type === UserType.SELLER;
    const [factorFromError, setFactorFromError] = useState<Factor>();

    const { mutateAsync: saveBankAccount, isLoading } = useMutation(async (payload: StripeBankAccount) => {
        if (payload?.id) {
            await api.put(
                `/sellers/${seller.id}/stripeAccount/${stripeAccount?.id}/bankAccounts/${payload.id}?seller=${seller.id}`,
                payload,
                config,
            );
        } else {
            await api.post(
                `/sellers/${seller.id}/stripeAccount/${stripeAccount?.id}/bankAccounts?seller=${seller.id}`,
                payload,
                config,
            );
        }

        invalidate("sellers", seller.id);
        flash.success("Bank account details updated successfully");
    });

    const handleSubmit = async (values: StripeBankAccount) => {
        setBankAccountPayload(values);
        if (hasMFAAccess) {
            trySaveBankAccount(values);
        }
    };

    const trySaveBankAccount = async (values: StripeBankAccount) => {
        try {
            await saveBankAccount(values);
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 450) {
                    setFactorFromError((error.response?.data as any)?.field?.factor);
                    showMFAModal.toggle();
                    return;
                }

                if (error.response?.status === 400) {
                    toggleshowMFAInfo();
                    return;
                }
            }

            throw new Error(error);
        }
    };

    const saveBankAccountFormDetails = async (code: string) => {
        if (code) {
            config.headers = { ...config.headers, "X-AUTH-CODE": code };
            if (bankAccountPayload) {
                await saveBankAccount(bankAccountPayload);
                setIsEditable(false);
                setBankAccountPayload(undefined);
            }
        }
    };

    const onClose = () => {
        setBankAccountPayload(undefined);
        showMFAModal.toggle();
    };

    if (!isEditable) {
        return (
            <div>
                <FormGroup>
                    <Label>Routing Number</Label>
                    <Input disabled value={stripeBankAccount?.routing_number ?? ""} className="fs-exclude" />
                </FormGroup>

                <FormGroup>
                    <Label>Account Number</Label>
                    <Input
                        disabled
                        value={stripeBankAccount?.last4 ? `*****${stripeBankAccount?.last4 ?? ""}` : ""}
                        className="fs-exclude"
                    />
                </FormGroup>

                <div>
                    <SecondaryButton onClick={() => setIsEditable(true)}>Edit</SecondaryButton>
                </div>
            </div>
        );
    }

    return (
        <>
            <XolaFormik
                validationSchema={validationSchema}
                initialValues={getInitialValues(seller)}
                onSubmit={handleSubmit}
                onReset={() => {
                    setIsEditable(!(stripeBankAccount?.routing_number ?? stripeBankAccount?.last4));
                }}
            >
                {({ handleSubmit, handleReset }: any) => (
                    <div>
                        <FormField name="routing_number" label="Routing Number">
                            <InputField isRequired className="fs-exclude" />
                        </FormField>

                        <FormField name="account_number" label="Account Number">
                            <InputField isRequired className="fs-exclude" />
                        </FormField>

                        <div>
                            <SubmitButton type="button" isLoading={isLoading} onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton type="reset" className="ml-3" onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </div>
                    </div>
                )}
            </XolaFormik>
            <MultiFactorAuthenticationModal
                authenticationFor={AuthenticationFor.CHANGE_BANK_DETAILS}
                isOpen={!!bankAccountPayload && showMFAModal.isVisible}
                factor={factorFromError}
                isSaving={isLoading}
                onClose={onClose}
                onSuccess={saveBankAccountFormDetails}
            />

            <MultiFactorAuthenticationInfoModal
                isOpen={showMFAInfo && isUserSeller}
                onClose={toggleshowMFAInfo}
                onConfigure={toggleshowMFAInfo}
            />
            <MultiFactorAuthenticationForBankInfoModal
                isOpen={showMFAInfo && !isUserSeller}
                onClose={toggleshowMFAInfo}
            />
        </>
    );
};
